import type { AccountNode, CrossLedgerLink, Entry } from '@taler/api-client'
import { accountKeys, entryKeys } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { useDisplayCurrency } from '@/app/display-currency'
import { indexAccounts, type AccountIndex } from '../internal/transaction-row.ts'
import { isIdempotencyPending, problemDetail } from '../internal/problem.ts'
import { type EntryDraft, toReplaceEntryInput } from '../internal/entry-detail.ts'
import { useCrossLedgerLinks } from './use-cross-ledger-links.ts'

export interface UseEntryDetailOptions {
  readonly ledgerId: () => string | undefined
  readonly entryId: () => string | undefined
  readonly enabled: () => boolean
}

interface MutationState {
  readonly isPending: ComputedRef<boolean>
  readonly errorDetail: ComputedRef<string | undefined>
  readonly errorPending: ComputedRef<boolean>
}

export interface UseEntryDetail {
  readonly entry: ComputedRef<Entry | undefined>
  readonly crossLedgerLink: ComputedRef<CrossLedgerLink | undefined>
  readonly accounts: ComputedRef<AccountIndex>
  readonly accountTree: ComputedRef<readonly AccountNode[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly save: MutationState
  readonly reverse: MutationState
  readonly remove: MutationState
  replace(draft: EntryDraft): Promise<void>
  reverseEntry(date?: LocalDate): Promise<void>
  deleteEntry(): Promise<void>
  retry(): void
}

export function useEntryDetail(options: UseEntryDetailOptions): UseEntryDetail {
  const api = useApi()
  const queryClient = useQueryClient()
  const { displayCurrency } = useDisplayCurrency()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.entryId() !== undefined,
  )

  const entryQuery = useQuery({
    queryKey: computed(() =>
      entryKeys.detail(options.ledgerId() ?? '', options.entryId() ?? '', displayCurrency.value),
    ),
    queryFn: ({ signal }) =>
      api.entries.get(options.ledgerId()!, options.entryId()!, displayCurrency.value, signal),
    enabled: active,
    retry: false,
  })

  const accountsQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const crossLedgerLinks = useCrossLedgerLinks({
    ledgerId: options.ledgerId,
    entryIds: () => (options.entryId() === undefined ? [] : [options.entryId()!]),
    enabled: () => active.value,
  })

  function invalidate(): void {
    for (const key of entryKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const replaceMutation = useMutation({
    mutationFn: (draft: EntryDraft) =>
      api.entries.replace(options.ledgerId()!, options.entryId()!, toReplaceEntryInput(draft)),
    onSuccess: invalidate,
  })

  const reverseMutation = useMutation({
    mutationFn: (date: LocalDate | undefined) =>
      api.entries.reverse(options.ledgerId()!, options.entryId()!, date),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.entries.delete(options.ledgerId()!, options.entryId()!),
    onSuccess: invalidate,
  })

  return {
    entry: computed(() => entryQuery.data.value),
    crossLedgerLink: computed(() =>
      options.entryId() === undefined ? undefined : crossLedgerLinks.value.get(options.entryId()!),
    ),
    accounts: computed(() => indexAccounts(accountsQuery.data.value ?? [])),
    accountTree: computed(() => accountsQuery.data.value ?? []),
    isLoading: computed(() => active.value && (entryQuery.isPending.value || accountsQuery.isPending.value)),
    error: computed(() => entryQuery.error.value ?? accountsQuery.error.value),
    retrying: retrying(entryQuery, accountsQuery),
    save: {
      isPending: computed(() => replaceMutation.isPending.value),
      errorDetail: computed(() => problemDetail(replaceMutation.error.value)),
      errorPending: computed(() => isIdempotencyPending(replaceMutation.error.value)),
    },
    reverse: {
      isPending: computed(() => reverseMutation.isPending.value),
      errorDetail: computed(() => problemDetail(reverseMutation.error.value)),
      errorPending: computed(() => isIdempotencyPending(reverseMutation.error.value)),
    },
    remove: {
      isPending: computed(() => deleteMutation.isPending.value),
      errorDetail: computed(() => problemDetail(deleteMutation.error.value)),
      errorPending: computed(() => isIdempotencyPending(deleteMutation.error.value)),
    },
    replace: async (draft) => {
      await replaceMutation.mutateAsync(draft)
    },
    reverseEntry: async (date) => {
      await reverseMutation.mutateAsync(date)
    },
    deleteEntry: () => deleteMutation.mutateAsync(),
    retry: () => {
      void entryQuery.refetch()
      void accountsQuery.refetch()
    },
  }
}
